import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { useRouter } from "next/router";
import Link from "next/link";
import useOutsideClickHider from "components/helpers/OutsideClickHider";
import useVendorUser from "hooks/use-vendor-user";
import { get } from "lodash";
import BusinessInfoIcon from "components/icons/storefront";
import ServicesIcon from "components/icons/filecabinet";
import AccountSettingsIcon from "components/icons/settings";
import SupportIcon from "components/icons/phone";
import PersonIcon from "components/icons/person";
import LogoutIcon from "components/icons/logout";
import FourSquareIcon from "components/icons/fourSquares";
// import TwoPeople from "components/icons/twopeople";
import Logo from "components/icons/logo";
import LogoMobile from "components/icons/logomobile";
import AppointmentsIcon from "components/icons/appointment";
import PersonCheckIcon from "components/icons/personcheck";
import WrenchIcon from "components/icons/wrench";
import HomeIcon from "components/icons/home";
import { BASE_API_URL } from "lib/constants";
import useVendorClient from "hooks/use-vendor-client";
import Profile from "components/Profile";
import GlobalSide from "components/GlobalSide";
import GlobalFooter from "components/GlobalFooter";
import Menu from "components/ProviderNavigationMenu";
import {
  GenericWrapper,
  Header,
  LogoWrapperLg,
  LogoWrapperMd,
} from "components/Shared/navigation";

const GridWrapper = styled.div`
  display: grid;
  min-height: 100vh;
  grid-template-columns: auto 1fr;
  grid-template-rows: 4.375rem auto 1fr;
  grid-template-areas:
    "header header"
    "main ${({ hideSide }) => (hideSide ? "main" : "sidebar")}"
    "footer footer";

  ${({ theme }) => theme.mq.md`
    grid-template-columns: 1fr 23.4375rem;
    grid-template-areas:
      "header header"
      "main main"
      "footer footer";
  `}

  ${({ theme }) => theme.mq.lg`
    grid-template-columns: 15.625rem 1fr;
    grid-template-rows: 4.375rem auto fit-content(15.625rem);
    grid-template-areas:
      "header header"
      "sidebar main"
      ". footer";
  `}
`;

const HeaderButtons = styled.div`
  margin: auto 1.875rem auto 0;
  display: flex;
`;

const Main = styled.div`
  grid-area: main;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;

  ${({ isGeneric }) =>
    isGeneric &&
    css`
      width: 100vw;
    `}
`;

const Overlay = styled.div`
  display: none;
  opacity: 0;
  overflow: hidden;

  ${({ isSideNavVisible }) =>
    !isSideNavVisible &&
    css`
      background-color: #5a5b64;
      display: block;
      height: 100%;
      left: 0;
      opacity: 0.5;
      position: fixed;
      top: 0;
      width: 100%;
      z-index: 3;
    `}
`;

const getProfileItems = (bid, home, userId) => {
  if (!userId) {
    return [];
  }
  if (!bid) {
    return [
      {
        id: 1,
        Icon: BusinessInfoIcon,
        text: "List Your Business",
        redirectUrl: "/provider/existing-search",
      },
      {
        id: 2,
        Icon: PersonIcon,
        text: "MY PROFILE",
        redirectUrl: "/provider/user-profile",
        userType: "provider",
      },
      {
        id: 3,
        Icon: SupportIcon,
        text: "CONTACT SUPPORT",
        redirectUrl: "mailto:support@goodfriend.com",
        userType: "provider",
      },
      {
        id: 4,
        Icon: LogoutIcon,
        text: "LOGOUT",
        isLogout: true,
        redirectUrl: "/provider/auth",
        userType: "provider",
      },
    ];
  }
  return [
    {
      id: 1,
      Icon: FourSquareIcon,
      text: "MY BUSINESS",
      redirectUrl: home,
      userType: "provider",
    },
    {
      id: 2,
      Icon: PersonIcon,
      text: "MY PROFILE",
      redirectUrl: "/provider/user-profile",
      userType: "provider",
    },
    {
      id: 3,
      Icon: SupportIcon,
      text: "CONTACT SUPPORT",
      redirectUrl: "mailto:support@goodfriend.com",
      userType: "provider",
    },
    {
      id: 4,
      Icon: LogoutIcon,
      text: "LOGOUT",
      isLogout: true,
      redirectUrl: "/provider/auth",
      userType: "provider",
    },
  ];
};

const urls = {
  "/": true,
  "/business/[rid]/[lid]/[fid]": true,
  "/cookie-policy": true,
  "/customer/auth": true,
  "/home/*/*": true,
  "/privacy-policy": true,
  "/provider/[uid]": true,
  "/provider/auth": true,
  "/provider/auth/email/error": true,
  "/provider/auth/email/success": true,
  "/provider/business-registration": true,
  "/provider/claim-status": true,
  "/provider/existing-search": true,
  "/provider/existing-search/business-redirect": true,
  "/provider/terms-service": true,
  "/search": true,
  "/customer/terms-service": true,
};

const genericUrls = (asPath) => {
  return urls[asPath];
};

const getSideBarItems = (
  bid,
  setupStatus = false,
  bizType = null,
  providerReadOnly = false
) => {
  if (!bid) {
    return [
      {
        id: 1,
        Icon: BusinessInfoIcon,
        text: "List Your Business",
        redirectUrl: "/provider/existing-search",
      },
    ];
  }

  let navItems = [];
  if (!setupStatus && !providerReadOnly) {
    navItems.push({
      id: 2,
      Icon: PersonCheckIcon,
      text: "Set Up Business",
      redirectUrl: `/provider/${bid || "*"}/dashboard/setup`,
    });
  } else if (!providerReadOnly) {
    navItems.push({
      id: 1,
      Icon: HomeIcon,
      text: "Home",
      redirectUrl: `/provider/${bid || "*"}/dashboard/home`,
      userType: "provider",
    });
  }
  navItems.push({
    id: 3,
    Icon: BusinessInfoIcon,
    text: "Business Profile",
    redirectUrl: `/provider/${bid || "*"}/dashboard/business-profile`,
  });
  if (!providerReadOnly && bizType !== "veterinary") {
    navItems = navItems.concat([
      {
        id: 4,
        Icon: AppointmentsIcon,
        text: "Appointments",
        redirectUrl: `/provider/${bid || "*"}/dashboard/appointments/calendar`,
      },
      {
        id: 5,
        Icon: ServicesIcon,
        text: "Services",
        redirectUrl: `/provider/${bid || "*"}/dashboard/services`,
      },
      {
        id: 6,
        Icon: AccountSettingsIcon,
        text: "Account Settings",
        redirectUrl: `/provider/${bid || "*"}/dashboard/settings/payment`,
      },
    ]);
  } else if (bizType === "veterinary") {
    navItems = navItems.concat([
      {
        id: 4,
        Icon: WrenchIcon,
        text: "Practice Details",
        redirectUrl: `/provider/${bid || "*"}/dashboard/practice-details`,
      },
    ]);
  }
  return navItems;
};

const footerSupportItem = {
  id: 1,
  Icon: SupportIcon,
  text: "Contact Support",
  redirectUrl: "mailto:support@goodfriend.com",
};

const ProviderNavigation = (props) => {
  const [hideSide, setHideSide] = useState(true);
  const sideNavRef = React.useRef(null);
  const IconEl = React.useRef(null);
  useOutsideClickHider([sideNavRef, IconEl], setHideSide);
  const vendorClient = useVendorClient();
  const router = useRouter();
  const { uid } = router.query;
  const vendorUser = useVendorUser();
  const bid = get(vendorUser, "business.id", undefined);
  const [sideBarItems, setSideBarItems] = useState([]);
  const [businessHome, setBusinessHome] = useState(
    `/provider/${bid}/dashboard/setup`
  );
  if (!vendorUser?.id) {
    return null;
  }
  const statusCheck = async () => {
    if (!bid) {
      const items = getSideBarItems(bid);
      setSideBarItems(items);
      return;
    }
    const params = {
      data: {
        data: {
          vendorBusiness: {
            id: bid,
            fields: ["setupStepsCompleted", "businessType", "providerReadOnly"],
          },
        },
      },
    };
    const res = await vendorClient.request({
      method: "get",
      url: `${BASE_API_URL}/deep-unrest/read`,
      params,
    });
    const setupStatus =
      res.data.vendorBusiness?.attributes?.setupStepsCompleted;
    const bizType = res.data.vendorBusiness?.attributes?.businessType;
    const providerReadOnly = res.data.vendorBusiness?.attributes?.providerReadOnly;
    if (setupStatus === true) {
      setBusinessHome(`/provider/${bid || "*"}/dashboard/home`);
    } else {
      setBusinessHome(`/provider/${bid || "*"}/dashboard/setup`);
    }
    const items = getSideBarItems(bid, setupStatus, bizType, providerReadOnly);
    setSideBarItems(items);
  };
  useEffect(() => { // eslint-disable-line
    statusCheck().catch((e) => {
      console.error(e);
    });
  }, []);
  const { children } = props;
  const providerId = uid;
  const profileItems = getProfileItems(bid, businessHome, vendorUser?.id);
  const generic = (
    <GenericWrapper hideSide={hideSide}>
      <Header aria-label="primary">
        <LogoWrapperLg>
          <Link href={`/?uid=${providerId}`}>
            <a aria-label="GoodFriend Logo">
              <Logo color="#27272f" />
            </a>
          </Link>
        </LogoWrapperLg>
        <LogoWrapperMd>
          <Link href={`/?uid=${providerId}`}>
            <a aria-label="GoodFriend Logo">
              <Logo color="#27272f" />
            </a>
          </Link>
        </LogoWrapperMd>
        <HeaderButtons>
          <Profile
            providerId={providerId}
            profileCardIdentity={vendorUser}
            profileItems={profileItems}
            isProviderNav
          />
          <Menu
            ref={IconEl}
            setHideSide={setHideSide}
            hideSide={hideSide}
            providerId={providerId}
          />
        </HeaderButtons>
      </Header>
      <Main initial={{ opacity: 0 }} animate={{ opacity: 1 }} isGeneric>
        <>
          <Overlay isSideNavVisible={hideSide} />
          {children}
        </>
      </Main>
      <GlobalFooter />
      <GlobalSide
        hideLg
        providerId={providerId}
        profileCardIdentity={vendorUser}
        footerSupportItem={footerSupportItem}
        profileItems={profileItems}
        sideBarItems={sideBarItems}
        ref={sideNavRef}
        hideSide={hideSide}
        isProviderNav
      />
    </GenericWrapper>
  );
  const nonGeneric = (
    <GridWrapper hideSide={hideSide}>
      <Header aria-label="primary">
        <LogoWrapperLg>
          <Link href={`/?uid=${providerId}`}>
            <a>
              <Logo color="#27272f" />
            </a>
          </Link>
        </LogoWrapperLg>
        <LogoWrapperMd>
          <Link href={`/?uid=${providerId}`}>
            <a>
              <LogoMobile color="#27272f" />
            </a>
          </Link>
        </LogoWrapperMd>
        <HeaderButtons>
          <Profile
            providerId={providerId}
            profileCardIdentity={vendorUser}
            profileItems={profileItems}
            isProviderNav
          />
          <Menu
            ref={IconEl}
            setHideSide={setHideSide}
            hideSide={hideSide}
            providerId={providerId}
          />
        </HeaderButtons>
      </Header>
      <Main initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <>
          <Overlay isSideNavVisible={hideSide} />
          {children}
        </>
      </Main>
      <GlobalSide
        hideLg
        providerId={providerId}
        profileCardIdentity={vendorUser}
        footerSupportItem={footerSupportItem}
        profileItems={profileItems}
        sideBarItems={sideBarItems}
        ref={sideNavRef}
        hideSide={hideSide}
        isProviderNav
      />
      <GlobalSide
        gridSide
        hideSm
        providerId={providerId}
        profileCardIdentity={vendorUser}
        profileItems={profileItems}
        sideBarItems={sideBarItems}
        footerSupportItem={footerSupportItem}
        hideSide={hideSide}
        ref={sideNavRef}
        isProviderNav
      />
      <GlobalFooter />
    </GridWrapper>
  );
  return genericUrls(router.pathname) ? generic : nonGeneric;
};

export default ProviderNavigation;

ProviderNavigation.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};
